import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Spinner, Typography } from '@material-tailwind/react';
import {
  Building2Icon, ChevronLeftIcon, ChevronRightIcon, ListIcon, MapIcon,
  PencilIcon, PlusIcon,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useGetBranches } from '../../custom-hooks/use-branches';
import { useAuth } from '../../services/contexts/authContext';
import { STATIC_USER_ROLES } from '../../utils/enums';
import ListModeMenu from './list-mode-menu';
import BranchCrudModal from './branch-crud-modal';
import { managementBranchUrl } from './urls';

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_API_KEY || 'pk.eyJ1IjoiZmFycnVraHNoYWhpZCIsImEiOiJjbGhobnEwNWQwYWJuM2duems5cTdmZ2F5In0.VqxGVO_hETuJnKw4-ej3xA';

const ManagementPage = () => {
  const navigate = useNavigate();
  const { userDetails } = useAuth();
  const canManageBranches = [STATIC_USER_ROLES.ADMIN, STATIC_USER_ROLES.SUPER_ADMIN].includes(userDetails?.role);

  const { data: branches, isLoading, error } = useGetBranches();
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [branchModalOpen, setBranchModalOpen] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [viewMode, setViewMode] = useState('map');

  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const markersRef = useRef([]);

  const sortedBranches = useMemo(
    () => [...(branches || [])].sort((a, b) => (a.branchName || '').localeCompare(b.branchName || '')),
    [branches]
  );

  const selectedIndex = useMemo(() => {
    if (!selectedBranch?._id) return -1;
    return sortedBranches.findIndex((b) => b._id === selectedBranch._id);
  }, [selectedBranch, sortedBranches]);

  useEffect(() => {
    if (!selectedBranch?._id || !branches) return;
    const fresh = branches.find((b) => b._id === selectedBranch._id);
    if (fresh) setSelectedBranch(fresh);
    else setSelectedBranch(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branches]);

  useEffect(() => {
    if (viewMode !== 'map' || !mapContainerRef.current || mapRef.current) return undefined;

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/light-v11',
      center: [74.3587, 31.5204],
      zoom: 5,
    });
    map.addControl(new mapboxgl.NavigationControl(), 'top-right');
    mapRef.current = map;

    return () => {
      markersRef.current.forEach((m) => m.remove());
      markersRef.current = [];
      map.remove();
      mapRef.current = null;
    };
  }, [viewMode]);

  useEffect(() => {
    const map = mapRef.current;
    if (viewMode !== 'map' || !map || !branches) return undefined;

    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];

    const coordinates = [];
    branches.forEach((branch) => {
      if (branch.lng == null || branch.lat == null) return;
      coordinates.push([branch.lng, branch.lat]);

      const el = document.createElement('button');
      el.type = 'button';
      el.className = 'management-branch-marker';
      el.setAttribute('aria-label', branch.branchName || 'Branch');
      el.style.cssText = [
        'width:28px',
        'height:28px',
        'border-radius:50%',
        'border:2px solid #fff',
        'background:#ea580c',
        'box-shadow:0 2px 8px rgba(0,0,0,.25)',
        'cursor:pointer',
        'padding:0',
      ].join(';');

      el.addEventListener('click', (e) => {
        e.stopPropagation();
        setSelectedBranch(branch);
        map.flyTo({ center: [branch.lng, branch.lat], zoom: Math.max(map.getZoom(), 12), essential: true });
      });

      const marker = new mapboxgl.Marker({ element: el })
        .setLngLat([branch.lng, branch.lat])
        .addTo(map);
      markersRef.current.push(marker);
    });

    if (coordinates.length > 0) {
      const bounds = coordinates.reduce(
        (b, coord) => b.extend(coord),
        new mapboxgl.LngLatBounds(coordinates[0], coordinates[0])
      );
      map.fitBounds(bounds, { padding: 60, maxZoom: 14, duration: 800 });
    }

    return undefined;
  }, [branches, viewMode]);

  const selectBranchByIndex = (index) => {
    const branch = sortedBranches[index];
    if (!branch) return;
    setSelectedBranch(branch);
    const map = mapRef.current;
    if (map && branch.lng != null && branch.lat != null) {
      map.flyTo({ center: [branch.lng, branch.lat], zoom: Math.max(map.getZoom(), 12), essential: true });
    }
  };

  const goToPreviousBranch = () => {
    if (selectedIndex <= 0) selectBranchByIndex(sortedBranches.length - 1);
    else selectBranchByIndex(selectedIndex - 1);
  };

  const goToNextBranch = () => {
    if (selectedIndex < 0 || selectedIndex >= sortedBranches.length - 1) selectBranchByIndex(0);
    else selectBranchByIndex(selectedIndex + 1);
  };

  const openAddBranch = () => {
    setEditingBranch(null);
    setBranchModalOpen(true);
  };

  const openManageBranch = () => {
    if (!selectedBranch) return;
    setEditingBranch(selectedBranch);
    setBranchModalOpen(true);
  };

  const enterBuildings = (branch = selectedBranch) => {
    if (!branch?._id) return;
    navigate(managementBranchUrl(branch._id));
  };

  return (
    <div>
      <div className="m-5 p-5 border rounded-3xl">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
          <h1 className="md:text-2xl font-thin">Management</h1>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex rounded-full border p-0.5">
              <button
                type="button"
                onClick={() => setViewMode('map')}
                className={`rounded-full px-3 py-1 text-xs flex items-center gap-1 ${viewMode === 'map' ? 'bg-gray-900 text-white' : 'text-gray-600'}`}
              >
                <MapIcon size={12} /> Map
              </button>
              <button
                type="button"
                onClick={() => setViewMode('list')}
                className={`rounded-full px-3 py-1 text-xs flex items-center gap-1 ${viewMode === 'list' ? 'bg-gray-900 text-white' : 'text-gray-600'}`}
              >
                <ListIcon size={12} /> List
              </button>
            </div>
            <ListModeMenu />
            {canManageBranches && (
              <Button
                size="sm"
                className="rounded-full flex items-center gap-2 normal-case font-normal"
                onClick={openAddBranch}
              >
                <PlusIcon size={14} />
                Add branch
              </Button>
            )}
          </div>
        </div>

        <Typography variant="small" className="text-gray-500 mb-3">
          {viewMode === 'map'
            ? 'Select a branch marker to manage it or enter its buildings.'
            : 'All branches — click a row to enter buildings or manage the branch.'}
        </Typography>

        {viewMode === 'map' ? (
          <div className="relative">
            <div
              ref={mapContainerRef}
              className="map-container rounded-3xl border w-full"
              style={{ height: 'min(70vh, 640px)' }}
            />

            {selectedBranch && (
              <div className="absolute left-4 bottom-4 right-4 md:left-auto md:right-4 md:bottom-4 md:w-80 z-10 rounded-2xl border bg-white shadow-lg p-4">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      className="p-1 rounded-full hover:bg-gray-100 text-gray-500"
                      onClick={goToPreviousBranch}
                      aria-label="Previous branch"
                    >
                      <ChevronLeftIcon size={18} />
                    </button>
                    <button
                      type="button"
                      className="p-1 rounded-full hover:bg-gray-100 text-gray-500"
                      onClick={goToNextBranch}
                      aria-label="Next branch"
                    >
                      <ChevronRightIcon size={18} />
                    </button>
                  </div>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-700 text-sm"
                    onClick={() => setSelectedBranch(null)}
                  >
                    Close
                  </button>
                </div>
                <div className="min-w-0 mb-2">
                  <Typography variant="h6" className="truncate">
                    {selectedBranch.branchName}
                  </Typography>
                  <Typography variant="small" className="text-gray-500">
                    {[selectedBranch.address, selectedBranch.city].filter(Boolean).join(', ')}
                  </Typography>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                  <Building2Icon size={14} />
                  <span>{selectedBranch.buildingCount ?? '—'} buildings</span>
                  {sortedBranches.length > 1 && (
                    <span className="text-gray-400 ml-auto text-xs">
                      {selectedIndex + 1} / {sortedBranches.length}
                    </span>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  {canManageBranches && (
                    <Button
                      size="sm"
                      variant="outlined"
                      className="rounded-full flex items-center justify-center gap-2 normal-case"
                      onClick={openManageBranch}
                    >
                      <PencilIcon size={14} />
                      Edit branch
                    </Button>
                  )}
                  <Button
                    size="sm"
                    className="rounded-full flex items-center justify-center gap-2 normal-case"
                    onClick={() => enterBuildings()}
                  >
                    <Building2Icon size={14} />
                    Manage buildings
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="rounded-2xl border overflow-hidden">
            {isLoading ? (
              <div className="flex justify-center py-12">
                <Spinner />
              </div>
            ) : sortedBranches.length === 0 ? (
              <div className="text-center py-10 text-gray-500">No branches yet.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-3">Branch</th>
                    <th className="px-4 py-3 hidden md:table-cell">Location</th>
                    <th className="px-4 py-3">Buildings</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {sortedBranches.map((branch) => (
                    <tr key={branch._id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{branch.branchName}</td>
                      <td className="px-4 py-3 text-gray-500 hidden md:table-cell">
                        {[branch.address, branch.city].filter(Boolean).join(', ') || '—'}
                      </td>
                      <td className="px-4 py-3">{branch.buildingCount ?? '—'}</td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          {canManageBranches && (
                            <Button
                              size="sm"
                              variant="outlined"
                              className="rounded-full normal-case text-xs"
                              onClick={() => {
                                setEditingBranch(branch);
                                setBranchModalOpen(true);
                              }}
                            >
                              Manage
                            </Button>
                          )}
                          <Button
                            size="sm"
                            className="rounded-full normal-case text-xs"
                            onClick={() => enterBuildings(branch)}
                          >
                            Buildings
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {viewMode === 'map' && isLoading && (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        )}
        {error && (
          <div className="text-center py-4 text-red-500 text-sm">
            Error loading branches. Please try again.
          </div>
        )}
        {viewMode === 'map' && !isLoading && branches?.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No branches yet.
            {canManageBranches && ' Add a branch to get started.'}
          </div>
        )}
      </div>

      <BranchCrudModal
        isOpen={branchModalOpen}
        onClose={() => {
          setBranchModalOpen(false);
          setEditingBranch(null);
        }}
        branch={editingBranch}
      />
    </div>
  );
};

export default ManagementPage;
